import React, { useState } from "react";
import { AlertTriangle, Crosshair, Loader2 } from "lucide-react";
import { getMediaBaseUrl } from "../../config/clientConfig";

/**
 * Point of Impact — where the model believes the collision landed, and which
 * panels it attributes to that impact.
 *
 * Driven by GET /poi_tracking, normalised by ../poiTracking.js.
 *
 * No coordinate marker is drawn over the image, deliberately. poi_coord is
 * declared in `original_image_pixels` at 1920×1080 (AR 1.778), but the served
 * /poi/ image is 742×598 (AR 1.241) — a different aspect ratio, so it is a CROP
 * of the original rather than a scaled copy. No linear transform maps the
 * coordinate onto it, and an overlay drawn from the normalised pair only appears
 * near the damage by luck. The image already carries the model's own annotations
 * baked in, which are authoritative; asserting a second, wrong position on top
 * of them would be worse than showing none.
 *
 * The coordinate is still reported numerically below the image, labelled with the
 * frame it refers to. If the API later exposes the uncropped frame (or the crop
 * rect), an overlay becomes possible — position it against the image's painted
 * content box, not its element box, since object-contain letterboxes.
 */

const TIER_STYLE = {
  primary: { ring: "#e2000f", bg: "rgba(226,0,15,0.10)", label: "Primary" },
  secondary: { ring: "#f09a56", bg: "rgba(240,154,86,0.12)", label: "Secondary" },
  tertiary: { ring: "#f3c65f", bg: "rgba(243,198,95,0.14)", label: "Tertiary" },
};

const CONFIDENCE_STYLE = {
  high: { fg: "#1c7a43", bg: "rgba(28,122,67,0.10)" },
  medium: { fg: "#9a6212", bg: "rgba(154,98,18,0.10)" },
  low: { fg: "#c22f2f", bg: "rgba(194,47,47,0.10)" },
};

const mediaUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = getMediaBaseUrl();
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

const Chip = ({ children, fg, bg, title }) => (
  <span
    title={title}
    className="inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[10.5px] font-semibold uppercase tracking-[0.04em]"
    style={{ color: fg, background: bg }}
  >
    {children}
  </span>
);

const card =
  "rounded-card border border-cl-card bg-cl-surface overflow-hidden";
const cardHead =
  "flex items-center justify-between gap-3 border-b border-cl-head px-4 py-3";
const cardTitle = "text-[13.5px] font-semibold text-cl-text";
const label =
  "text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label";

export default function PointOfImpact({ poi, loading }) {
  const [imageBroken, setImageBroken] = useState(false);

  if (loading) {
    return (
      <div className={`${card} flex items-center justify-center py-16`}>
        <div className="flex items-center gap-2 text-[13px] text-cl-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading point of impact…
        </div>
      </div>
    );
  }

  if (!poi) {
    return (
      <div className={`${card} px-4 py-10 text-center`}>
        <div className="text-[13.5px] font-semibold text-cl-text">
          No point-of-impact analysis
        </div>
        <p className="mt-1 text-[12px] text-cl-muted">
          This case has no POI record.
        </p>
      </div>
    );
  }

  // Pending and failed are distinct from "no POI": say which, and why.
  if (!poi.isComplete) {
    const failed = poi.status === "failed";
    return (
      <div className={`${card} px-4 py-10 text-center`}>
        <div className="flex items-center justify-center gap-2 text-[13.5px] font-semibold text-cl-text">
          {failed ? (
            <AlertTriangle className="h-4 w-4 text-[#c22f2f]" />
          ) : (
            <Loader2 className="h-4 w-4 animate-spin text-cl-muted" />
          )}
          {failed ? "Point-of-impact analysis failed" : "Analysis in progress"}
        </div>
        <p className="mt-1 text-[12px] text-cl-muted">
          {poi.errorMessage ||
            (failed
              ? "No reason was reported."
              : "The impact point is still being computed for this case.")}
        </p>
        {poi.status && (
          <p className="mt-2 text-[11px] text-cl-faint">Status: {poi.status}</p>
        )}
      </div>
    );
  }

  const conf = CONFIDENCE_STYLE[poi.confidence] || CONFIDENCE_STYLE.medium;

  const summary = poi.summary;

  return (
    <div className="flex flex-col gap-3">
      {/* Assessment summary — headline, description and the model's own stated
          limitations, all from damage_summary in the parsed poi_result. Kept
          together and above the evidence, because the headline frames what the
          image is showing rather than commenting on a detail of it. */}
      {summary && (summary.headline || summary.description || summary.limitations.length > 0) && (
        <div className={card}>
          <div className={cardHead}>
            <span className={cardTitle}>Assessment summary</span>
            {summary.confidence && (
              <Chip
                fg={(CONFIDENCE_STYLE[summary.confidence] || CONFIDENCE_STYLE.medium).fg}
                bg={(CONFIDENCE_STYLE[summary.confidence] || CONFIDENCE_STYLE.medium).bg}
              >
                {summary.confidence} confidence
              </Chip>
            )}
          </div>

          <div className="px-4 py-[15px]">
            {summary.headline && (
              <p className="text-[14.5px] font-semibold leading-[1.45] tracking-[-0.01em] text-cl-text">
                {summary.headline}
              </p>
            )}
            {summary.description && (
              <p className="mt-2 text-[12.5px] leading-[1.55] text-cl-secondary">
                {summary.description}
              </p>
            )}
          </div>

          {summary.limitations.length > 0 && (
            <div className="border-t border-cl-head px-4 py-[13px]">
              <div className={label}>
                Limitations — not assessed
              </div>
              <ul className="mt-[6px] grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
                {summary.limitations.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-[6px] text-[12px] text-cl-muted"
                  >
                    <span aria-hidden="true" className="mt-[7px] h-[3px] w-[3px] shrink-0 rounded-full bg-cl-disabled" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-3 lg:flex-row">
      {/* Model-annotated evidence image */}
      <div className={`${card} min-w-0 flex-1`}>
        <div className={cardHead}>
          <div className="flex items-center gap-2">
            <Crosshair className="h-[15px] w-[15px] text-cl-accent" />
            <span className={cardTitle}>Impact point</span>
          </div>
          {poi.confidence && (
            <Chip fg={conf.fg} bg={conf.bg}>
              {poi.confidence} confidence
            </Chip>
          )}
        </div>

        <div className="bg-cl-canvas">
          {poi.image && !imageBroken ? (
            <img
              src={mediaUrl(poi.image)}
              alt="Point of impact — model-annotated evidence"
              className="block max-h-[420px] w-full object-contain"
              onError={() => setImageBroken(true)}
            />
          ) : (
            <div className="px-4 py-12 text-center text-[12px] text-cl-muted">
              {poi.image
                ? "Evidence image could not be loaded."
                : "No evidence image was selected."}
            </div>
          )}
        </div>

        {poi.marker && (
          <div className="border-t border-cl-head px-4 py-2 text-[11px] text-cl-faint">
            Impact coordinate {poi.marker.x}, {poi.marker.y} in the original{" "}
            {poi.marker.imageWidth}×{poi.marker.imageHeight} frame · the image
            above is the model's annotated crop
          </div>
        )}
      </div>

      {/* Attribution */}
      <div className="flex w-full min-w-0 flex-col gap-3 lg:w-[380px] lg:shrink-0">
        <div className={card}>
          <div className={cardHead}>
            <span className={cardTitle}>Impact attribution</span>
            {poi.reviewRequired && (
              <Chip
                fg="#c22f2f"
                bg="rgba(194,47,47,0.10)"
                title="The model asked for a human check"
              >
                <AlertTriangle className="h-3 w-3" />
                Review
              </Chip>
            )}
          </div>

          <div className="flex flex-col gap-3 px-4 py-[13px]">
            {poi.tiers.length > 0 ? (
              poi.tiers.map(({ tier, parts }) => {
                const style = TIER_STYLE[tier] || TIER_STYLE.tertiary;
                return (
                  <div key={tier} className="flex items-start gap-2">
                    <span
                      className="mt-[3px] h-[10px] w-[10px] shrink-0 rounded-full"
                      style={{
                        background: style.bg,
                        border: `2px solid ${style.ring}`,
                      }}
                    />
                    <div className="min-w-0">
                      <div className={label}>{style.label}</div>
                      <div className="text-[13px] font-medium text-cl-text">
                        {parts.join(", ")}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-[12px] text-cl-muted">
                No panels were attributed to the impact.
              </p>
            )}
          </div>

          {poi.explanation && (
            <div className="border-t border-cl-head px-4 py-[13px]">
              <div className={label}>Reasoning</div>
              <p className="mt-1 text-[12.5px] leading-[1.55] text-cl-secondary">
                {poi.explanation}
              </p>
            </div>
          )}
        </div>

        {poi.partDecisions.length > 0 && (
          <div className={card}>
            <div className={cardHead}>
              <span className={cardTitle}>Part decisions</span>
            </div>
            <div className="divide-y divide-cl-row">
              {poi.partDecisions.map((part) => (
                <div
                  key={part.rawPartName}
                  className="flex items-center justify-between gap-3 px-4 py-[10px]"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[12.5px] font-medium text-cl-text">
                      {part.partName}
                    </div>
                    {part.tier && (
                      <div className="text-[10.5px] uppercase tracking-[0.04em] text-cl-faint">
                        {TIER_STYLE[part.tier]?.label || part.tier}
                      </div>
                    )}
                  </div>
                  {part.reviewRequired ? (
                    <Chip fg="#c22f2f" bg="rgba(194,47,47,0.10)">
                      Needs review
                    </Chip>
                  ) : (
                    <span className="shrink-0 text-[12px] capitalize text-cl-secondary">
                      {(part.decision || "—").replace(/_/g, " ")}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
      </div>
    </div>
  );
}
